import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";

const STATUSES = ["scheduled", "played", "postponed", "cancelled"];
const emptyFixture = {
  date: "", time: "", opponent: "", home_away: "home",
  venue: "", competition: "LFA Third Division", status: "scheduled",
  score_for: "", score_against: "",
};

const fmtDate = (v) => { try { return new Date(v + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return String(v); } };

/**
 * Admin CRUD for ESAFC match fixtures + results.
 */
export default function AdminFixtures() {
  const [fixtures, setFixtures] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyFixture);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/admin/fixtures", { params: { page: 1, limit: 100 } });
      setFixtures(data.items);
    } catch {
      setFixtures([]);
    }
  }

  useEffect(() => { load(); }, []);

  function openNew() {
    setEditing(null);
    setForm(emptyFixture);
    setShowForm(true);
  }

  function openEdit(f) {
    setEditing(f);
    setForm({
      date: f.date || "", time: f.time || "", opponent: f.opponent || "",
      home_away: f.home_away || "home", venue: f.venue || "",
      competition: f.competition || "LFA Third Division", status: f.status || "scheduled",
      score_for: f.score_for ?? "", score_against: f.score_against ?? "",
    });
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
    setForm(emptyFixture);
  }

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      score_for: form.status === "played" && form.score_for !== "" ? Number(form.score_for) : null,
      score_against: form.status === "played" && form.score_against !== "" ? Number(form.score_against) : null,
    };
    try {
      if (editing) {
        await api.put(`/admin/fixtures/${editing.id}`, payload);
        toast.success("Fixture updated.");
      } else {
        await api.post("/admin/fixtures", payload);
        toast.success("Fixture added.");
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the fixture.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(f) {
    if (!window.confirm(`Delete the ${f.opponent} fixture? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/fixtures/${f.id}`);
      toast.success("Fixture deleted.");
      load();
    } catch {
      toast.error("Could not delete.");
    }
  }

  return (
    <div data-testid="admin-fixtures-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl">ESAFC Fixtures &amp; Results</h3>
        <button onClick={openNew} data-testid="admin-fixture-new-btn" className="inline-flex items-center gap-2 bg-palm text-white font-bold rounded-full px-5 py-2.5 hover:shadow-xl transition">
          <Plus size={16} /> New fixture
        </button>
      </div>

      {fixtures === null && <div className="p-8 text-ink/60">Loading…</div>}
      {fixtures && fixtures.length === 0 && !showForm && (
        <div className="p-8 border border-dashed border-ink/15 rounded-[14px] text-ink/60 text-center">
          No fixtures yet. Add the first match to show it on the public ESAFC page.
        </div>
      )}
      {fixtures && fixtures.length > 0 && (
        <div className="overflow-auto border border-ink/10 rounded-[14px] bg-white" data-testid="admin-fixtures-table">
          <table className="min-w-full text-sm">
            <thead className="bg-ink/5">
              <tr>
                {["Date", "Opponent", "H/A", "Competition", "Status", "Score", ""].map((h) => (
                  <th key={h} className="text-left font-semibold p-3 border-b border-ink/10">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fixtures.map((f) => (
                <tr key={f.id} className="border-b border-ink/5 hover:bg-mango/10">
                  <td className="p-3 align-top whitespace-nowrap">{fmtDate(f.date)}{f.time ? ` · ${f.time}` : ""}</td>
                  <td className="p-3 align-top font-semibold">{f.opponent}</td>
                  <td className="p-3 align-top">{f.home_away === "home" ? "Home" : "Away"}</td>
                  <td className="p-3 align-top text-ink/70">{f.competition}</td>
                  <td className="p-3 align-top">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      f.status === "played" ? "bg-palm text-white"
                      : f.status === "scheduled" ? "bg-mango text-palm-deep"
                      : f.status === "postponed" ? "bg-berry text-white"
                      : "bg-ink/10 text-ink/70"}`}>
                      {f.status}
                    </span>
                  </td>
                  <td className="p-3 align-top font-display text-base">
                    {f.status === "played" && f.score_for != null ? `${f.score_for} – ${f.score_against}` : "—"}
                  </td>
                  <td className="p-3 align-top text-right whitespace-nowrap">
                    <button onClick={() => openEdit(f)} data-testid={`admin-fixture-edit-${f.id}`} className="text-palm font-bold text-sm mr-3 hover:underline inline-flex items-center gap-1"><Pencil size={13} /> Edit</button>
                    <button onClick={() => remove(f)} data-testid={`admin-fixture-delete-${f.id}`} className="text-berry inline-flex items-center gap-1 font-bold text-sm hover:underline"><Trash2 size={14} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-start justify-center overflow-auto p-6" data-testid="admin-fixture-form">
          <form onSubmit={save} className="bg-white rounded-[14px] w-full max-w-[640px] p-8 mt-10">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl">{editing ? "Edit fixture" : "New fixture"}</h3>
              <button type="button" onClick={closeForm} data-testid="admin-fixture-close-btn" className="text-ink/60 hover:text-berry">
                <X size={22} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold block mb-1">Date</label>
                <input required type="date" value={form.date} onChange={set("date")} data-testid="admin-fixture-date" className="w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Kickoff time</label>
                <input type="time" value={form.time} onChange={set("time")} data-testid="admin-fixture-time" className="w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
              </div>
            </div>

            <label className="text-sm font-semibold block mb-1 mt-3">Opponent</label>
            <input required value={form.opponent} onChange={set("opponent")} data-testid="admin-fixture-opponent" placeholder="e.g. Brewerville United" className="w-full rounded-md border border-ink/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-palm" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold block mb-1">Home / Away</label>
                <select value={form.home_away} onChange={set("home_away")} data-testid="admin-fixture-homeaway" className="w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm bg-white">
                  <option value="home">Home</option>
                  <option value="away">Away</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Competition</label>
                <input value={form.competition} onChange={set("competition")} data-testid="admin-fixture-competition" className="w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
              </div>
            </div>

            <label className="text-sm font-semibold block mb-1 mt-3">Venue</label>
            <input value={form.venue} onChange={set("venue")} data-testid="admin-fixture-venue" placeholder="e.g. Mango Town Field" className="w-full rounded-md border border-ink/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-palm" />

            <label className="text-sm font-semibold block mb-1">Status</label>
            <select value={form.status} onChange={set("status")} data-testid="admin-fixture-status" className="w-full rounded-md border border-ink/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-palm bg-white">
              {STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </select>

            {form.status === "played" && (
              <div className="grid grid-cols-2 gap-3 mb-1">
                <div>
                  <label className="text-sm font-semibold block mb-1">ESAFC score</label>
                  <input required type="number" min="0" max="99" value={form.score_for} onChange={set("score_for")} data-testid="admin-fixture-score-for" className="w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
                </div>
                <div>
                  <label className="text-sm font-semibold block mb-1">Opponent score</label>
                  <input required type="number" min="0" max="99" value={form.score_against} onChange={set("score_against")} data-testid="admin-fixture-score-against" className="w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button disabled={saving} data-testid="admin-fixture-save-btn" className="bg-palm text-white font-bold rounded-full px-6 py-3 hover:shadow-xl transition disabled:opacity-60 inline-flex items-center gap-2">
                {saving && <Loader2 size={15} className="animate-spin" />}
                {saving ? "Saving…" : editing ? "Save changes" : "Add fixture"}
              </button>
              <button type="button" onClick={closeForm} className="border-2 border-ink/15 text-ink font-bold rounded-full px-6 py-3 hover:border-berry hover:text-berry transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
